/**
 * Formatting utility functions
 */

import { VehicleInfo, ShopSchedule } from "../types";

/**
 * Substitute {{var}} placeholders in a template with values from an object.
 *
 * Performs plain text interpolation — no HTML escaping is applied to the
 * substituted values. The returned string is intended to be rendered as a
 * JSX text node, where React handles output escaping. Do NOT pipe the result
 * into `dangerouslySetInnerHTML`: the values are not sanitized for HTML
 * contexts and would be an XSS vector there.
 *
 * Replaces the prior Handlebars-based helper to avoid pre-marking values as
 * `SafeString`, which would make any future HTML sink unsafe.
 *
 * `null`/`undefined` values render as empty strings; numbers are coerced via
 * `String()`. Unrecognized placeholders are left in the output as-is.
 */
export const compileTemplate = (
  template: string,
  values: Record<string, string | number | null | undefined>
): string =>
  template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
    if (!Object.prototype.hasOwnProperty.call(values, key)) return match;
    const value = values[key];
    return value == null ? "" : String(value);
  });

/**
 * Formats a phone number to (xxx) xxx-xxxx format
 * @param {string} phoneNumber - The phone number to format
 * @returns {string|false} - The formatted phone number or false if invalid
 */
export const formatPhoneNumber = function (
  phoneNumber: string
): string | false {
  // Format phone number to (xxx) xxx-xxxx
  const phoneNumberPattern = /^\+?1?(\d{3})(\d{3})(\d{4})$/;
  if (phoneNumberPattern.test(phoneNumber)) {
    const formattedPhoneNumber = phoneNumber.replace(
      phoneNumberPattern,
      "($1) $2-$3"
    );
    return formattedPhoneNumber;
  } else {
    return false;
  }
};

/**
 * Extracts vehicle ID from vehicle object
 * @param {VehicleInfo} vehicle - The vehicle object
 * @returns {string|null} - The vehicle ID or null if not found
 */
export const getVehicleId = (
  vehicle: VehicleInfo | undefined
): string | null => {
  if (
    vehicle &&
    Array.isArray(vehicle.crossRef) &&
    vehicle.crossRef.length > 0
  ) {
    return vehicle.crossRef[0]?.crossRef || null;
  }
  return null;
};

/**
 * Interface for location-like objects with search parameters
 */
export interface LocationLike {
  search: string;
}

/**
 * Parses URL query parameters
 * @param {LocationLike} location - Location object with search parameters
 * @returns {Record<string, string>} - Object containing all parsed parameters
 */
export const parseQueryString = (
  location: LocationLike
): Record<string, string> => {
  const searchParams = new URLSearchParams(location.search);
  const params: Record<string, string> = {};
  for (const [key, value] of Array.from(searchParams.entries())) {
    params[key] = value;
  }
  return params;
};

/**
 * Day name abbreviations indexed by schedule day number (0 = Sunday).
 */
const DAY_NAMES: Record<number, string> = {
  0: "Sun",
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
};

/**
 * Converts a 24-hour time string like "09:00" or "18:00" into a
 * compact 12-hour string like "9am" or "6pm".
 */
const formatTime12h = (time?: string): string => {
  if (!time) return "";
  const [hourStr, minuteStr] = time.split(":");
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr || "0", 10);
  if (isNaN(hour)) return time;
  const suffix = hour >= 12 ? "pm" : "am";
  hour = hour % 12 || 12;
  if (minute > 0) {
    return `${hour}:${minuteStr}${suffix}`;
  }
  return `${hour}${suffix}`;
};

const toTimeString = (
  hour: string | number | undefined,
  minute: string | number | undefined
): string => {
  const parsedHour = parseInt(String(hour ?? ""), 10);
  const parsedMinute = parseInt(String(minute ?? ""), 10);

  if (isNaN(parsedHour)) return "";
  const minutePart = isNaN(parsedMinute) ? 0 : parsedMinute;
  return `${String(parsedHour).padStart(2, "0")}:${String(minutePart).padStart(2, "0")}`;
};

export const getScheduleTimes = (entry: ShopSchedule): { openTime: string; closeTime: string } => {
  const openTime = entry.openTime || toTimeString(entry.openHour, entry.openMin);
  const closeTime =
    entry.closeTime || toTimeString(entry.closedHour, entry.closedMin);

  return { openTime, closeTime };
};

/**
 * Formats an array of ShopSchedule entries into a compact hours-of-operation
 * string that groups consecutive days with the same hours.
 *
 * Example outputs:
 *   "Mon \u2013 Fri: 9am \u2013 6pm"
 *   "Mon \u2013 Fri: 9am \u2013 6pm  |  Sat: 10am \u2013 5pm"
 */
export const formatHoursOfOperation = (
  schedule?: ShopSchedule[]
): string => {
  if (!schedule || schedule.length === 0) return "";

  // Sort by day so we process Mon(1) → Sat(6), Sun(0) last
  const sorted = [...schedule]
    .filter((s) => s.open)
    .sort((a, b) => {
      // Treat Sunday (0) as 7 so it appears after Saturday
      const dayA = a.day === 0 ? 7 : a.day;
      const dayB = b.day === 0 ? 7 : b.day;
      return dayA - dayB;
    });

  if (sorted.length === 0) return "";

  // Build groups of consecutive days with identical open/close times
  interface DayGroup {
    days: number[];
    openTime: string;
    closeTime: string;
  }

  const groups: DayGroup[] = [];

  for (const entry of sorted) {
    const { openTime, closeTime } = getScheduleTimes(entry);
    const last = groups[groups.length - 1];
    const entryDayOrder = entry.day === 0 ? 7 : entry.day;

    if (
      last &&
      last.openTime === openTime &&
      last.closeTime === closeTime
    ) {
      const lastDayOrder = last.days[last.days.length - 1] === 0 ? 7 : last.days[last.days.length - 1];
      if (entryDayOrder === lastDayOrder + 1) {
        last.days.push(entry.day);
        continue;
      }
    }

    groups.push({ days: [entry.day], openTime, closeTime });
  }

  // Format each group into a string
  return groups
    .map((g) => {
      const first = DAY_NAMES[g.days[0]] || "";
      const last = DAY_NAMES[g.days[g.days.length - 1]] || "";
      const dayRange = g.days.length > 1 ? `${first} \u2013 ${last}` : first;
      return `${dayRange}: ${formatTime12h(g.openTime)} \u2013 ${formatTime12h(g.closeTime)}`;
    })
    .join("  |  ");
};
